import { parseISO } from 'date-fns';
import type { ComponentProps } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useMediaQuery, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';

// Helper function to normalize string dates to Date objects
const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    try {
      return parseISO(value);
    } catch {
      return null;
    }
  }
  return null;
};

// ----------------------------------------------------------------------

export function RHFDatePicker({ name, slotProps, required, ...other }: any) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          {...field}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? slotProps?.textField?.helperText,
              required: required,
            },
            field: {
              clearable: true,
              ...slotProps?.field,
            },
          }}
          value={normalizeDate(field.value)}
          onChange={(date) => field.onChange(date)}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFTimePickerProps = Omit<
  ComponentProps<typeof DesktopTimePicker>,
  'value' | 'onChange' | 'name'
> & {
  name: string;
  required?: boolean;
};

export function RHFTimePicker({
  name,
  slotProps,
  required,
  ampm = false,
  ...other
}: RHFTimePickerProps) {
  const { control } = useFormContext();

  // MUI slot props also accept a callback form; helperText merging supports only the
  // object form (a callback spread into the object literal was already a no-op).
  const textFieldSlotProps =
    typeof slotProps?.textField === 'function' ? undefined : slotProps?.textField;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        // Always use the desktop variant: its digital clock commits and closes
        // on selection. The responsive `TimePicker` falls back to the mobile
        // variant on touch devices, which relies on the OK action bar to close
        // — and we intentionally drop that bar for date-picker parity, leaving
        // the mobile picker with no way to close on selection.
        <DesktopTimePicker
          {...field}
          ampm={ampm}
          slotProps={{
            ...slotProps,
            textField: {
              ...textFieldSlotProps,
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? textFieldSlotProps?.helperText,
              required: required,
            },
            field: {
              clearable: true,
              ...slotProps?.field,
            },
            // Commit on selection like the date pickers — no OK/Cancel action bar.
            actionBar: { actions: [], ...slotProps?.actionBar },
          }}
          value={normalizeDate(field.value)}
          onChange={(date) => field.onChange(date)}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFDateTimePicker({ name, slotProps, required, ampm = false, ...other }: any) {
  const { control } = useFormContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const Picker = isMobile ? MobileDateTimePicker : DesktopDateTimePicker;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <Picker
          {...field}
          ampm={ampm}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? slotProps?.textField?.helperText,
              required: required,
            },
            field: {
              clearable: true,
              ...slotProps?.field,
            },
          }}
          value={normalizeDate(field.value)}
          onChange={(date) => field.onChange(date)}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

/**
 * @deprecated Prefer `RHFDateTimePicker` — it now switches to the mobile
 * variant automatically below the `sm` breakpoint.
 */
export function RHFMobileDateTimePicker({ name, slotProps, required, ...other }: any) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <MobileDateTimePicker
          {...field}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? slotProps?.textField?.helperText,
              required: required,
            },
          }}
          value={normalizeDate(field.value)}
          onChange={(date) => field.onChange(date)}
          {...other}
        />
      )}
    />
  );
}
